// =============================================================================
// supplier-team-client.http.ts — Adaptateur HTTP RÉEL du SupplierTeamClient (DBU §3.7, F4).
// =============================================================================
// Câble la SPA contre les endpoints FOURNISSEUR du BC supplier-reference (§3.7). Substitué derrière le
// MÊME DI seam (SupplierTeamClientProvider) : écrans/hooks consomment l'interface SupplierTeamClient,
// l'impl est substituée sans toucher l'UI.
//
// SÉCURITÉ (RULE_REF_006) : le secret de session fournisseur (cookie httpOnly `supplier_session`) n'est
// JAMAIS lu ni fabriqué ici — porté par le cookie (credentials: 'include'). Le siret ET l'identité MASTER
// (invitant) viennent de la session côté API (jamais du body — INVARIANT n°1). AUCUNE donnée métier en
// localStorage.
//
// CSRF DOUBLE-SUBMIT FOURNISSEUR (TenantInterceptor, supplier-cookies.ts) : sur les MUTATIONS (POST
// invite/revoke), l'en-tête `x-csrf-token` DOIT égaler le cookie NON-httpOnly `supplier_csrf` (parité
// rate-collection-client.http.ts). Le GET (annuaire) n'exige AUCUN en-tête CSRF. Sans l'en-tête, toute
// écriture fournisseur est 403 (fail-closed serveur, jamais d'écriture).
//
// MAPPING ERREURS par STATUS HTTP (relais du message serveur) : 401 session, 403 garde (ACL/[F1]/CSRF),
// 404 ressource, 409 conflit (unicité email), 400 valeur invalide (mur légal domaine, nom/prénom vides).

import {
  SupplierTeamApiError,
  type ContactTokenStatusDto,
  type InviteCollaboratorInput,
  type InviteCollaboratorResultDto,
  type RevokeCollaboratorInput,
  type SupplierTeamApiErrorKind,
  type SupplierTeamClient,
  type SupplierTeamDto,
} from '@/api/supplier-team-client';

/** Forme du corps d'erreur NestJS (HttpException standard) — message éventuellement string[]. */
interface NestErrorBody {
  readonly statusCode?: number;
  readonly message?: string | readonly string[];
  readonly error?: string;
}

/** Nom du cookie CSRF NON-httpOnly FOURNISSEUR (cf. API supplier-cookies.ts SUPPLIER_CSRF_COOKIE). */
const SUPPLIER_CSRF_COOKIE_NAME = 'supplier_csrf';

/** Mapping STATUS HTTP → kind UX (le mur légal domaine remonte en 400 → 'invalid-value'). */
const STATUS_TO_KIND: Readonly<Record<number, SupplierTeamApiErrorKind>> = {
  400: 'invalid-value',
  401: 'unauthorized',
  403: 'forbidden',
  404: 'not-found',
  409: 'conflict',
  // Renvoi de lien d'activation : plafond anti-abus 3/24h (ActivationResendRateLimitError → 429).
  429: 'rate-limited',
};

/**
 * Lit la valeur du cookie CSRF fournisseur `supplier_csrf` (NON-httpOnly, lisible par le SPA même
 * origine). `undefined` si absent (pas de session, ou contexte sans `document`) — l'appel mutant partira
 * alors sans en-tête et l'API répondra 403 (fail-closed serveur).
 */
function readSupplierCsrfToken(): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }
  for (const part of document.cookie.split(';')) {
    const [rawName, ...rest] = part.trim().split('=');
    if (rawName === SUPPLIER_CSRF_COOKIE_NAME) {
      return decodeURIComponent(rest.join('='));
    }
  }
  return undefined;
}

/**
 * Adaptateur HTTP réel : appels `fetch` contre l'API fournisseur équipe. `baseUrl` par défaut vide
 * (chemins relatifs : SPA et API partagent l'origine, ou un reverse proxy route `/supplier/*`).
 */
export class HttpSupplierTeamClient implements SupplierTeamClient {
  public constructor(private readonly baseUrl: string = '/api') {}

  public async listTeam(): Promise<SupplierTeamDto> {
    const response = await fetch(`${this.baseUrl}/supplier/contacts`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
    });
    if (!response.ok) {
      throw await this.toApiError(response);
    }
    return (await response.json()) as SupplierTeamDto;
  }

  public async listTokenStatus(): Promise<readonly ContactTokenStatusDto[]> {
    const response = await fetch(`${this.baseUrl}/supplier/contacts/token-status`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
    });
    if (!response.ok) {
      throw await this.toApiError(response);
    }
    return (await response.json()) as readonly ContactTokenStatusDto[];
  }

  public async resendActivation(contactId: string): Promise<void> {
    const response = await this.mutate(
      `/supplier/contacts/${encodeURIComponent(contactId)}/resend-activation`,
      {},
    );
    if (!response.ok) {
      throw await this.toApiError(response);
    }
  }

  public async inviteCollaborator(
    input: InviteCollaboratorInput,
  ): Promise<InviteCollaboratorResultDto> {
    const response = await this.mutate('/supplier/contacts/invite', {
      collaboratorEmail: input.collaboratorEmail,
      nom: input.nom,
      prenom: input.prenom,
    });
    if (!response.ok) {
      throw await this.toApiError(response);
    }
    return (await response.json()) as InviteCollaboratorResultDto;
  }

  public async revokeCollaborator(input: RevokeCollaboratorInput): Promise<void> {
    const response = await this.mutate('/supplier/contacts/revoke', {
      contactId: input.contactId,
      reason: input.reason,
    });
    if (!response.ok) {
      throw await this.toApiError(response);
    }
  }

  /** POST JSON avec CSRF double-submit fournisseur (`x-csrf-token` = cookie `supplier_csrf`). */
  private async mutate(path: string, body: Record<string, unknown>): Promise<Response> {
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    const csrf = readSupplierCsrfToken();
    if (csrf !== undefined) {
      headers['x-csrf-token'] = csrf;
    }
    return fetch(`${this.baseUrl}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(body),
    });
  }

  private async toApiError(response: Response): Promise<SupplierTeamApiError> {
    const body = await this.safeJson(response);
    const message =
      this.extractMessage(body) ?? `Erreur API (${String(response.status)}).`;
    const kind = STATUS_TO_KIND[response.status] ?? 'generic';
    return new SupplierTeamApiError(message, kind);
  }

  private extractMessage(body: NestErrorBody | undefined): string | undefined {
    const message = body?.message;
    if (message === undefined) {
      return undefined;
    }
    return typeof message === 'string' ? message : message.join(' ');
  }

  private async safeJson(response: Response): Promise<NestErrorBody | undefined> {
    try {
      return (await response.json()) as NestErrorBody;
    } catch {
      return undefined;
    }
  }
}
